import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, RefreshCw, Loader2, LogIn, Layers } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import BuildClientColumn from '../../components/admin/BuildClientColumn';
import BuildBatchBar from '../../components/admin/BuildBatchBar';
import BuildFilterBar from '../../components/admin/BuildFilterBar';
import AdminNav from '../../components/admin/AdminNav';

// ─── Auth helper (same pattern as AdminDashboard) ─────────────────────────────

function getPassword() {
  return sessionStorage.getItem('admin_password') || '';
}

// ─── Phase row labels ─────────────────────────────────────────────────────────

const PHASE_ROW_LABELS = [
  { id: 'setup',         label: 'Phase 1 — Setup' },
  { id: 'customization', label: 'Phase 2 — Customization' },
  { id: 'delivery',      label: 'Phase 3 — Delivery' },
];

// ─── BuildBoard ───────────────────────────────────────────────────────────────

const BuildBoard = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState(getPassword());
  const [inputPw, setInputPw] = useState('');
  const [authed, setAuthed] = useState(!!getPassword());

  const [builds, setBuilds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);

  const channelRef = useRef(null);

  // ─── Fetch ──────────────────────────────────────────────────────────────────

  const fetchBuilds = useCallback(async (pw = password) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/get-build-board?password=${encodeURIComponent(pw)}`, {
        headers: { 'x-admin-password': pw },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch');
      setBuilds(data.builds || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [password]);

  // ─── Auth ───────────────────────────────────────────────────────────────────

  const handleLogin = (e) => {
    e.preventDefault();
    sessionStorage.setItem('admin_password', inputPw);
    setPassword(inputPw);
    setAuthed(true);
  };

  // ─── Realtime ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!authed) return;
    fetchBuilds();

    const channel = supabase
      .channel('build-board-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'build_packages' }, () => {
        fetchBuilds();
      })
      .subscribe();

    channelRef.current = channel;
    return () => { supabase.removeChannel(channel); };
  }, [authed, fetchBuilds]);

  // ─── Start Build ────────────────────────────────────────────────────────────

  const handleStartBuild = async (buildId) => {
    try {
      const res = await fetch('/api/update-build-phase', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-admin-password': password },
        body: JSON.stringify({ build_package_id: buildId, phase: 'setup' }),
      });
      if (!res.ok) throw new Error('Failed to start build');
      fetchBuilds();
    } catch (err) {
      alert(err.message);
    }
  };

  // ─── Batch done callback ────────────────────────────────────────────────────

  const handleBatchDone = () => {
    fetchBuilds();
    setSelectedIds([]);
  };

  // ─── Filter + counts ────────────────────────────────────────────────────────

  const counts = builds.reduce((acc, b) => {
    acc[b.phase] = (acc[b.phase] || 0) + 1;
    return acc;
  }, {});

  const filtered = filter === 'all' ? builds : builds.filter(b => b.phase === filter);

  // ─── Toggle select ──────────────────────────────────────────────────────────

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  // ─── Login screen ────────────────────────────────────────────────────────────

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0A0A14] flex items-center justify-center p-4">
        <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-2xl p-8 w-full max-w-sm">
          <div className="flex items-center gap-2 mb-6">
            <Shield className="w-5 h-5 text-[#7B61FF]" />
            <span className="font-bold text-[#F0EFF4]">Build Board</span>
          </div>
          <form onSubmit={handleLogin} className="space-y-3">
            <input
              type="password"
              value={inputPw}
              onChange={e => setInputPw(e.target.value)}
              placeholder="Admin password"
              className="w-full bg-[#F0EFF4]/5 border border-[#F0EFF4]/10 rounded-lg px-4 py-3 text-[#F0EFF4] placeholder-[#F0EFF4]/30 outline-none focus:border-[#7B61FF]/50"
              autoFocus
            />
            <button
              type="submit"
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#7B61FF] hover:bg-[#6B51EF] text-white font-bold rounded-lg transition-colors"
            >
              <LogIn className="w-4 h-4" /> Enter
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ─── Main board ──────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-[#0A0A14] text-[#F0EFF4] flex flex-col">
      {/* Batch bar (sticky, only when builds selected) */}
      {selectedIds.length > 0 && (
        <BuildBatchBar
          selectedBuildIds={selectedIds}
          password={password}
          onDone={handleBatchDone}
        />
      )}

      {/* Top bar */}
      <div className="border-b border-[#F0EFF4]/10 px-6 py-4 space-y-3 bg-[#0A0A14]">
        <AdminNav />
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-[#7B61FF]" />
              <h1 className="font-bold text-lg">Build Board</h1>
            </div>
            <span className="text-xs font-data text-[#F0EFF4]/40">{filtered.length} build{filtered.length !== 1 ? 's' : ''}</span>
          </div>

          <div className="flex items-center gap-3">
            <BuildFilterBar activeFilter={filter} onChange={setFilter} counts={counts} />
            <button
              onClick={() => fetchBuilds()}
              disabled={loading}
              className="p-2 rounded-lg hover:bg-[#F0EFF4]/10 text-[#F0EFF4]/40 hover:text-[#F0EFF4] transition-colors"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mx-6 mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}

      {loading && builds.length === 0 && (
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#7B61FF]" />
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="flex-1 flex items-center justify-center text-[#F0EFF4]/30 text-sm">
          No builds matching filter
        </div>
      )}

      {filtered.length > 0 && (
        <div className="flex-1 overflow-x-auto">
          <div className="flex min-w-max">
            {/* Phase label column */}
            <div className="w-32 shrink-0 border-r border-[#F0EFF4]/10">
              {/* Header spacer */}
              <div className="border-b border-[#F0EFF4]/10 px-3 py-3 h-[105px] flex items-end pb-2">
                <span className="text-[11px] font-data text-[#F0EFF4]/30 uppercase tracking-wider">Phase</span>
              </div>
              {/* Phase rows */}
              {PHASE_ROW_LABELS.map(row => (
                <div key={row.id} className="px-3 py-3 border-b border-[#F0EFF4]/5 last:border-0 h-16 flex items-center">
                  <span className="text-xs font-data text-[#F0EFF4]/50 leading-tight">{row.label}</span>
                </div>
              ))}
            </div>

            {/* Client columns */}
            {filtered.map(build => (
              <div
                key={build.id}
                className={`relative cursor-pointer ${selectedIds.includes(build.id) ? 'ring-1 ring-[#7B61FF]/50 bg-[#7B61FF]/5' : ''}`}
                onClick={e => {
                  // Only toggle select if clicking the header checkbox area (shift+click or meta+click)
                  if (e.shiftKey || e.metaKey) { e.preventDefault(); toggleSelect(build.id); }
                }}
              >
                <BuildClientColumn
                  build={build}
                  onStartBuild={handleStartBuild}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default BuildBoard;
